import { useEffect, useState, useRef } from 'react';
import { Layout, List, Input, Button, Card, Avatar, Typography, Modal, Form, message, Select, Space, Mentions, Spin, Tooltip, Badge, Dropdown, Grid, Divider } from 'antd';
import { CheckOutlined, SendOutlined, PlusOutlined, UserOutlined, TeamOutlined, PaperClipOutlined, CloseOutlined, RollbackOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, ForwardOutlined, DeleteOutlined, BgColorsOutlined, ArrowLeftOutlined, PushpinOutlined, MessageOutlined, CheckSquareOutlined, EyeOutlined, AuditOutlined } from '@ant-design/icons';
import LinkifyText from '../components/LinkifyText';
import { useAuth } from '../store/auth';
import { ChatService } from '../api/services/chat.service';
import { UsersService } from '../api/services/users.service';
import { FilesService } from '../api/services/files.service';
import { TasksService } from '../api/services/tasks.service';
import { ProjectsService } from '../api/services/projects.service';
import UniversalFileUploadModal from '../components/UniversalFileUploadModal';
import { useFileViewer } from '../store/FileViewerContext';
import { useNotifications } from '../store/NotificationContext';
import ArchiveMedia from '../components/ArchiveMedia';

const SYSTEM_CHAT_ICONS = {
  system_mentions: <MessageOutlined />,
  system_my_tasks: <CheckSquareOutlined />,
  system_curating: <EyeOutlined />,
  system_validator: <AuditOutlined />,
};

const SYSTEM_CHAT_COLORS = {
  system_mentions: '#722ed1',
  system_my_tasks: '#1890ff',
  system_curating: '#52c41a',
  system_validator: '#fa8c16',
};

const formatMessageTime = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;
    
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return timeStr;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month} ${timeStr}`;
  } catch (e) {
    console.warn('Error formatting message time:', e, dateString);
    return '';
  }
};

const FilePreview = ({ fileId }) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { openFileCard } = useFileViewer();

  useEffect(() => {
    FilesService.getFileCard(fileId)
      .then(res => {
        setFileInfo(res.data.file);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fileId]);

  if (loading) {
    return <div style={{ marginTop: 8, padding: 8 }}><Spin size="small" /> Загрузка файла...</div>;
  }

  if (!fileInfo) {
    return (
      <div style={{ marginTop: 8, padding: 8, background: 'rgba(0,0,0,0.02)', border: '1px dashed #d9d9d9', borderRadius: 4, cursor: 'pointer' }} onClick={() => openFileCard(fileId)}>
        <FileOutlined style={{ marginRight: 8 }} />
        <span>Открыть файл</span>
      </div>
    );
  }

  const isImage = fileInfo.content_type?.startsWith('image/');
  
  if (isImage) {
    return (
      <div style={{ marginTop: 8 }}>
        <ArchiveMedia
          src={`/api/v1/files/${fileId}/content`}
          alt={fileInfo.original_name}
          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: 8, cursor: 'pointer' }}
          onClick={() => openFileCard(fileId)}
        />
        <div style={{ fontSize: '0.8em', color: 'gray', marginTop: 4 }}>{fileInfo.original_name}</div>
      </div>
    );
  }

  let FileIcon = FileOutlined;
  if (fileInfo.content_type === 'application/pdf') FileIcon = FilePdfOutlined;
  else if (fileInfo.content_type?.includes('word')) FileIcon = FileWordOutlined;
  else if (fileInfo.content_type?.includes('excel') || fileInfo.content_type?.includes('spreadsheet')) FileIcon = FileExcelOutlined;

  return (
    <div onClick={() => openFileCard(fileId)} style={{ display: 'block', marginTop: 8, cursor: 'pointer', color: 'inherit' }}>
      <div style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.04)', border: '1px solid #d9d9d9', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <FileIcon style={{ fontSize: '24px', color: '#1890ff' }} />
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{fileInfo.original_name}</div>
          <div style={{ fontSize: '0.8em', color: 'gray' }}>{(fileInfo.size / 1024).toFixed(1)} KB</div>
        </div>
      </div>
    </div>
  );
};

export default function ChatPage() {
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [readStatuses, setReadStatuses] = useState({});
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [directModalOpen, setDirectModalOpen] = useState(false);
  const [groupForm] = Form.useForm();
  const [directForm] = Form.useForm();
  const { user } = useAuth();
  const messagesEnd = useRef(null);
  const sentMessageIds = useRef(new Set());

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [currentPrefix, setCurrentPrefix] = useState('@');
  const [forwardModalOpen, setForwardModalOpen] = useState(false);
  const [msgToForward, setMsgToForward] = useState(null);
  const [forwardForm] = Form.useForm();
  const [sending, setSending] = useState(false);
  
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { unreadRooms, markRoomAsRead, fetchUnreadCounts, registerChatCallback, setCurrentOpenRoom } = useNotifications();

  const avatarUrl = (avatarId) => avatarId ? `/api/v1/files/${avatarId}/content` : null;

  const getDirectChatName = (room) => {
    if (!room.is_direct || !room.member_ids || !user) return null;
    const otherUserId = room.member_ids.find(id => id !== user.id) || user.id;
    const otherUser = users.find(u => String(u.id) === String(otherUserId));
    return otherUser ? (otherUser.full_name || otherUser.email) : null;
  };

  const loadRooms = () => {
    ChatService.getRooms().then(r => setRooms(r.data)).catch(() => {});
  };

  useEffect(() => {
    ChatService.initSystemChats().catch(() => {});
    loadRooms();
    UsersService.getUsers().then(r => setUsers(r.data)).catch(() => {});
    TasksService.getTasks().then(r => setTasks(r.data)).catch(() => {});
    ProjectsService.getProjects().then(r => setProjects(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const markAllRoomsAsRead = async () => {
    // пометить прочитанными все системные чаты одним кликом
    try {
      const sysRooms = rooms.filter(isSystemChat);
      await Promise.all(sysRooms.map(r => ChatService.readRoom(r.id)));
      loadRooms();
      fetchUnreadCounts();
    } catch (e) {
      console.error('markAllRoomsAsRead', e);
    }
  };

  useEffect(() => {
    if (currentRoom) {
      ChatService.getMessages(currentRoom.id).then(r => setMessages(r.data.reverse())).catch(() => {});
      markRoomAsRead(currentRoom.id);
      setReadStatuses(currentRoom.read_statuses || {});
      sentMessageIds.current.clear();
      setCurrentOpenRoom(currentRoom.id);
    } else {
      setCurrentOpenRoom(null);
    }
  }, [currentRoom]);

  useEffect(() => {
    if (!currentRoom) return;

    const currentRoomId = currentRoom.id;

    const handleIncomingMessage = ({ message: newMsg, roomId, refresh }) => {
      if (roomId !== currentRoomId) return;

      // ntfy 2.x не форвардит payload сообщений — тихо перечитываем из API
      if (refresh) {
        ChatService.getMessages(currentRoomId)
          .then(r => setMessages(r.data.reverse()))
          .catch(() => {});
        return;
      }

      if (newMsg.type === 'color_change') {
        setMessages(prev => prev.map(m => m.id === newMsg.message_id ? { ...m, color: newMsg.color } : m));
        return;
      }
      if (newMsg.type === 'read_receipt') {
        setReadStatuses(prev => ({
          ...prev,
          [newMsg.user_id]: newMsg.last_read_message_id
        }));
        return;
      }

      if (sentMessageIds.current.has(newMsg.id)) {
        sentMessageIds.current.delete(newMsg.id);
        return;
      }

      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });

      if (newMsg.sender_id !== user?.id) {
        markRoomAsRead(currentRoomId);
      }
    };

    const unregister = registerChatCallback(handleIncomingMessage);

    return () => {
      unregister();
    };
  }, [currentRoom, user]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || !currentRoom || sending) return;
    setSending(true);
    try {
      const res = await ChatService.sendMessage(currentRoom.id, { 
        content: inputValue,
        reply_to_id: replyTo ? replyTo.id : null 
      });
      const newMsg = res.data;
      
      sentMessageIds.current.add(newMsg.id);
      
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
      
      setInputValue('');
      setReplyTo(null);
    } catch (e) {
      message.error("Не удалось отправить сообщение");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async ({ file, title, fileType, projectId, description }) => {
    if (!currentRoom || sending) return;
    setSending(true);
    try {
      const uploadRes = await FilesService.uploadChatFile(
        currentRoom.id, title, fileType, projectId, description, file
      );
      
      const msgRes = await ChatService.sendMessage(currentRoom.id, {
        content: `Прикреплен файл: ${title}`,
        file_id: uploadRes.data.id,
        reply_to_id: replyTo ? replyTo.id : null
      });

      const newMsg = msgRes.data;
      
      sentMessageIds.current.add(newMsg.id);
      
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
      
      setUploadModalOpen(false);
      setReplyTo(null);
      message.success("Файл отправлен");
    } catch (e) {
      message.error("Ошибка загрузки файла");
    } finally {
      setSending(false);
    }
  };

  const handleCreateGroupRoom = async (values) => {
    try {
      const res = await ChatService.createRoom({ 
        name: values.name, 
        member_ids: values.member_ids || [],
        is_direct: false
      });
      setRooms(prev => [res.data, ...prev]);
      setGroupModalOpen(false);
      groupForm.resetFields();
      message.success('Групповой чат создан');
    } catch (e) {
      message.error("Ошибка при создании чата");
    }
  };

  const handleCreateDirectRoom = async (values) => {
    try {
      const selectedUser = users.find(u => String(u.id) === String(values.user_id));
      const name = selectedUser ? (selectedUser.full_name || selectedUser.email) : 'Личный чат';
      const res = await ChatService.createRoom({ 
        name: name, 
        member_ids: [values.user_id],
        is_direct: true
      });
      setRooms(prev => [res.data, ...prev]);
      setDirectModalOpen(false);
      directForm.resetFields();
      message.success('Личный чат создан');
    } catch (e) {
      message.error("Ошибка при создании чата");
    }
  };

  const handleTogglePin = async (roomId) => {
    try {
      const res = await ChatService.togglePinRoom(roomId);
      setRooms(prev => prev.map(r => r.id === roomId ? { ...r, pinned: res.data.pinned } : r));
      if (currentRoom?.id === roomId) {
        setCurrentRoom(prev => ({ ...prev, pinned: res.data.pinned }));
      }
      message.success(res.data.pinned ? 'Чат закреплен' : 'Чат откреплен');
    } catch (e) {
      message.error("Ошибка при закреплении чата");
    }
  };

  const isSystemChat = (room) => room.room_type && room.room_type.startsWith('system_');

  const pinnedRooms = rooms.filter(r => r.pinned || isSystemChat(r));
  const otherRooms = rooms.filter(r => !r.pinned && !isSystemChat(r));

  const renderRoomItem = (room) => {
    let displayName = room.name;
    let otherUser = null;
    if (room.is_direct && room.member_ids && user) {
      const otherUserId = room.member_ids.find(id => id !== user.id) || user.id;
      otherUser = users.find(u => String(u.id) === String(otherUserId));
      const directName = getDirectChatName(room);
      if (directName) displayName = directName;
    }

    const sysIcon = isSystemChat(room) ? SYSTEM_CHAT_ICONS[room.room_type] : null;
    const sysColor = isSystemChat(room) ? SYSTEM_CHAT_COLORS[room.room_type] : null;

    return (
      <List.Item
        onClick={() => setCurrentRoom(room)}
        style={{ padding: '8px 16px', cursor: 'pointer', background: currentRoom?.id === room.id ? '#e6f4ff' : 'transparent' }}
      >
        <List.Item.Meta 
          avatar={
            <Badge count={unreadRooms[room.id] || 0} size="small" offset={[0, 5]}>
              <Avatar 
                src={room.is_direct && otherUser?.avatar_file_id ? avatarUrl(otherUser.avatar_file_id) : null} 
                icon={
                  sysIcon ? (
                    <span style={{ color: sysColor }}>{sysIcon}</span>
                  ) : room.is_direct && !otherUser?.avatar_file_id ? (
                    <UserOutlined />
                  ) : !room.is_direct && !sysIcon ? (
                    <TeamOutlined />
                  ) : null
                } 
                style={sysColor ? { border: `2px solid ${sysColor}` } : {}}
              />
            </Badge>
          } 
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{displayName}</span>
              {!isSystemChat(room) && (
                <Tooltip title={room.pinned ? 'Открепить' : 'Закрепить'}>
                  <PushpinOutlined
                    rotate={-45}
                    style={{ color: room.pinned ? '#1890ff' : '#d9d9d9', fontSize: '12px', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePin(room.id);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          } 
        />
      </List.Item>
    );
  };

  return (
    <div className="page-container" style={{ height: 'calc(100vh - 140px)' }}>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h1 className="page-title">С.Чат</h1>
        <p className="page-subtitle">Системные уведомления · общение — в Nextcloud Talk (пункт «Чат»)</p>
      </div>
      
      <Layout style={{ height: 'calc(100% - 60px)', overflow: 'hidden', borderRadius: 16, background: 'transparent' }}>
        {(!isMobile || !currentRoom) && (
          <Layout.Sider width={isMobile ? '100%' : 280} style={{ background: '#fff', borderRight: '1px solid #e2e8f0', borderRadius: '16px 0 0 16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '12px 14px', fontSize: 12, color: '#64748b', lineHeight: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                Упоминания, задачи, статусы согласований.<br />
                <a href="/talk" onClick={(e) => { e.preventDefault(); window.location.href = '/talk'; }}>
                  Общение с коллегами → Nextcloud Talk
                </a>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ padding: '8px 16px 4px', fontSize: '11px', color: '#888', fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Системные чаты</span>
                <Button
                  size="small"
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => markAllRoomsAsRead()}
                  disabled={!rooms.some(r => (r.unread_count || 0) > 0)}
                  title="Пометить все системные уведомления прочитанными"
                >
                  Прочитать все
                </Button>
              </div>
              <List
                dataSource={rooms.filter(isSystemChat)}
                renderItem={renderRoomItem}
              />
              {rooms.filter(isSystemChat).length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  Системных чатов пока нет
                </div>
              )}
            </div>
          </div>
        </Layout.Sider>
      )}
      {(!isMobile || currentRoom) && (
        <Layout.Content style={{ display: 'flex', flexDirection: 'column', padding: '8px 16px', overflow: 'hidden' }}>
          {currentRoom ? (() => {
          let roomName = currentRoom.name;
          const directName = getDirectChatName(currentRoom);
          if (directName) {
            roomName = directName;
          }
          if (isSystemChat(currentRoom)) {
            const sysIcon = SYSTEM_CHAT_ICONS[currentRoom.room_type];
            const sysColor = SYSTEM_CHAT_COLORS[currentRoom.room_type];
            roomName = (
              <Space>
                <span style={{ color: sysColor }}>{sysIcon}</span>
                <span>{currentRoom.name}</span>
              </Space>
            );
          }
          return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Space>
                {isMobile && (
                  <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setCurrentRoom(null)} />
                )}
                <Typography.Title level={4} style={{ margin: 0 }}>{roomName}</Typography.Title>
              </Space>
              {user?.role_level >= 5 ? (
                <Dropdown
                  menu={{
                    items: [
                      { key: 'hide', label: 'Скрыть из моего списка', danger: false },
                      { key: 'delete', label: 'Удалить для всех', danger: true },
                    ],
                    onClick: ({ key }) => {
                      if (key === 'hide') {
                        Modal.confirm({
                          title: 'Скрыть чат?',
                          content: 'Чат будет удален из вашего списка, но если в него напишут новое сообщение, он появится вновь.',
                          okText: 'Скрыть',
                          cancelText: 'Отмена',
                          onOk: async () => {
                            try {
                              await ChatService.deleteRoom(currentRoom.id, false);
                              message.success('Чат скрыт');
                              setRooms(prev => prev.filter(r => r.id !== currentRoom.id));
                              setCurrentRoom(null);
                            } catch (e) {
                              message.error('Ошибка при скрытии чата');
                            }
                          }
                        });
                      } else if (key === 'delete') {
                        Modal.confirm({
                          title: 'Удалить чат?',
                          content: 'Это действие необратимо. Все сообщения будут удалены для всех участников.',
                          okText: 'Удалить',
                          okType: 'danger',
                          cancelText: 'Отмена',
                          onOk: async () => {
                            try {
                              await ChatService.deleteRoom(currentRoom.id, true);
                              message.success('Чат удален для всех');
                              setRooms(prev => prev.filter(r => r.id !== currentRoom.id));
                              setCurrentRoom(null);
                            } catch (e) {
                              message.error('Ошибка при удалении чата');
                            }
                          }
                        });
                      }
                    }
                  }}
                  trigger={['click']}
                >
                  <Button danger type="text" icon={<DeleteOutlined />}>
                    Удалить чат
                  </Button>
                </Dropdown>
              ) : (
                <Button 
                  danger 
                  type="text" 
                  icon={<DeleteOutlined />} 
                  onClick={() => {
                    Modal.confirm({
                      title: 'Удалить чат?',
                      content: 'Чат будет удален из вашего списка. Если в него напишут новое сообщение, он появится вновь.',
                      okText: 'Удалить',
                      okType: 'danger',
                      cancelText: 'Отмена',
                      onOk: async () => {
                        try {
                          await ChatService.deleteRoom(currentRoom.id, false);
                          message.success('Чат удален');
                          setRooms(prev => prev.filter(r => r.id !== currentRoom.id));
                          setCurrentRoom(null);
                        } catch (e) {
                          message.error('Ошибка при удалении чата');
                        }
                      }
                    });
                  }}
                >
                  Удалить чат
                </Button>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
              {messages.map((msg, i) => {
                const isMine = msg.sender_id === user?.id;
                const repliedMsg = msg.reply_to_id ? messages.find(m => m.id === msg.reply_to_id) : null;
                const sender = users.find(u => String(u.id) === String(msg.sender_id));
                const senderName = sender ? (sender.full_name || sender.email) : 'Система';
                
                let checkmarks = null;
                if (isMine) {
                  const others = (currentRoom.member_ids || []).filter(id => id !== user.id);
                  const readers = others.filter(id => readStatuses[id] >= msg.id);
                  
                  if (others.length === 0 || readers.length === 0) {
                    checkmarks = <span style={{color: 'gray', marginLeft: 4}}>✓</span>;
                  } else if (readers.length < others.length) {
                    const readerNames = readers.map(id => {
                      const u = users.find(u => String(u.id) === String(id));
                      return u ? (u.full_name || u.email) : 'Пользователь';
                    }).join(', ');
                    checkmarks = (
                      <Tooltip title={`Прочитано: ${readerNames}`}>
                        <span style={{color: 'gray', marginLeft: 4}}>✓✓</span>
                      </Tooltip>
                    );
                  } else {
                    const readerNames = readers.map(id => {
                      const u = users.find(u => String(u.id) === String(id));
                      return u ? (u.full_name || u.email) : 'Пользователь';
                    }).join(', ');
                    checkmarks = (
                      <Tooltip title={`Прочитано: ${readerNames}`}>
                        <span style={{color: '#1890ff', marginLeft: 4}}>✓✓</span>
                      </Tooltip>
                    );
                  }
                }
                
                let cardBg = isMine ? '#e6f4ff' : '#fff';
                let cardBorder = '1px solid #f0f0f0';
                
                if (msg.color === 'green') {
                  cardBg = '#f6ffed';
                  cardBorder = '1px solid #b7eb8f';
                } else if (msg.color === 'red') {
                  cardBg = '#fff2f0';
                  cardBorder = '1px solid #ffccc7';
                }

                return (
                  <div key={msg.id || i} style={{ marginBottom: 12, display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8 }}>
                    <Tooltip title={senderName}>
                      <Avatar 
                        src={sender?.avatar_file_id ? avatarUrl(sender.avatar_file_id) : null} 
                        icon={!sender?.avatar_file_id && <UserOutlined />} 
                        size="default"
                        style={{ flexShrink: 0 }}
                      />
                    </Tooltip>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
                      {!isMine && (
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: 2, marginLeft: 4 }}>
                          {senderName}
                        </div>
                      )}
                      <Card 
                        size="small" 
                        style={{ textAlign: 'left', background: cardBg, border: cardBorder }}
                      >
                      {repliedMsg && (
                        <div style={{ background: 'rgba(0,0,0,0.04)', padding: '4px 8px', borderLeft: '3px solid #1890ff', marginBottom: 8, fontSize: '0.85em', color: 'gray' }}>
                          В ответ на: {repliedMsg.content}
                        </div>
                      )}
                      
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        <LinkifyText text={msg.content} />
                      </div>

                      {msg.file_id && (
                        <FilePreview fileId={msg.file_id} />
                      )}
                      </Card>
                      <div style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginTop: 2 }}>
                        <span style={{ fontSize: '10px', color: '#888', padding: '0 4px' }}>
                          {formatMessageTime(msg.created_at)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginTop: 2, alignItems: 'center' }}>
                        <Tooltip title="Ответить">
                          <Button type="text" size="small" style={{ fontSize: '11px', padding: '0 4px', height: '20px', lineHeight: '20px', opacity: 0.6 }} icon={<RollbackOutlined />} onClick={() => setReplyTo(msg)} />
                        </Tooltip>
                        <Tooltip title="Переслать">
                          <Button type="text" size="small" style={{ fontSize: '11px', padding: '0 4px', height: '20px', lineHeight: '20px', opacity: 0.6 }} icon={<ForwardOutlined />} onClick={() => { setMsgToForward(msg); setForwardModalOpen(true); }} />
                        </Tooltip>
                        {(user?.role_level >= 5 && !currentRoom.is_direct) && (
                          <Dropdown
                            menu={{
                              items: [
                                { key: 'green', label: 'Выделить зеленым' },
                                { key: 'red', label: 'Выделить красным' },
                                { type: 'divider' },
                                { key: 'none', label: 'Снять выделение' },
                              ],
                              onClick: async ({ key }) => {
                                try {
                                  const color = key === 'none' ? null : key;
                                  setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, color } : m));
                                  await ChatService.updateMessageColor(msg.id, color);
                                } catch (e) {
                                  message.error('Ошибка изменения цвета');
                                }
                              }
                            }}
                            trigger={['click']}
                          >
                            <Tooltip title="Цвет">
                              <Button type="text" size="small" style={{ fontSize: '11px', padding: '0 4px', height: '20px', lineHeight: '20px', opacity: 0.6 }} icon={<BgColorsOutlined />} />
                            </Tooltip>
                          </Dropdown>
                        )}
                        {isMine && checkmarks}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEnd} />
            </div>
            
            {replyTo && (
              <div style={{ padding: '8px 12px', background: '#fafafa', borderLeft: '3px solid #1890ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85em', color: '#595959' }}>Ответ на: {replyTo.content.substring(0, 50)}...</span>
                <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setReplyTo(null)} />
              </div>
            )}
            
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Button icon={<PaperClipOutlined />} onClick={() => setUploadModalOpen(true)} disabled={sending} />
              <Mentions
                style={{ flex: 1 }}
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="Сообщение... Используйте @ для людей, # для задач, $ для проектов"
                value={inputValue}
                onChange={setInputValue}
                prefix={['@', '#', '$']}
                onSearch={(_, prefix) => setCurrentPrefix(prefix)}
                disabled={sending}
                options={[
                  ...(currentPrefix === '@' ? users.map(u => ({ value: u.full_name || u.email, label: u.full_name || u.email, key: `u_${u.id}`, prefix: '@' })) : []),
                  ...(currentPrefix === '#' ? tasks.map(t => ({ value: t.title, label: t.title, key: `t_${t.id}`, prefix: '#' })) : []),
                  ...(currentPrefix === '$' ? projects.map(p => ({ value: p.name, label: p.name, key: `p_${p.id}`, prefix: '$' })) : [])
                ]}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={sending} disabled={sending} />
            </div>
          </>
          );
        })() : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography.Text type="secondary">Выберите чат</Typography.Text>
          </div>
        )}
      </Layout.Content>
      )}

      <UniversalFileUploadModal 
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onUpload={handleFileUpload}
      />

      <Modal title="Новый групповой чат" open={groupModalOpen} onCancel={() => setGroupModalOpen(false)} onOk={() => groupForm.submit()} okText="Создать">
        <Form form={groupForm} layout="vertical" onFinish={handleCreateGroupRoom}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="member_ids" label="Участники">
            <Select
              mode="multiple"
              placeholder="Выберите сотрудников"
              options={users.filter(u => u.id !== user?.id).map(u => ({ value: u.id, label: u.full_name || u.email }))}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Новый личный чат" open={directModalOpen} onCancel={() => setDirectModalOpen(false)} onOk={() => directForm.submit()} okText="Создать">
        <Form form={directForm} layout="vertical" onFinish={handleCreateDirectRoom}>
          <Form.Item name="user_id" label="Сотрудник" rules={[{ required: true, message: 'Выберите сотрудника' }]}>
            <Select
              placeholder="Выберите сотрудника"
              options={users.filter(u => u.id !== user?.id).map(u => ({ value: u.id, label: u.full_name || u.email }))}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              showSearch
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Переслать сообщение" open={forwardModalOpen} onCancel={() => setForwardModalOpen(false)} onOk={() => forwardForm.submit()} okText="Переслать">
        <Form form={forwardForm} layout="vertical" onFinish={async (values) => {
          try {
            const sender = users.find(u => String(u.id) === String(msgToForward?.sender_id));
            const senderName = sender ? (sender.full_name || sender.email) : 'Пользователь';
            const content = `[Переслано от ${senderName}]:\n${msgToForward?.content}`;
            
            let roomIdToUse = values.target_id;
            
            if (typeof values.target_id === 'string' && values.target_id.startsWith('user_')) {
              const userId = parseInt(values.target_id.replace('user_', ''), 10);
              const targetUser = users.find(u => String(u.id) === String(userId));
              const name = targetUser ? (targetUser.full_name || targetUser.email) : 'Личный чат';
              
              const res = await ChatService.createRoom({ 
                name: name, 
                member_ids: [userId],
                is_direct: true
              });
              roomIdToUse = res.data.id;
              
              setRooms(prev => {
                if (!prev.some(r => r.id === res.data.id)) return [res.data, ...prev];
                return prev;
              });
            } else if (typeof values.target_id === 'string' && values.target_id.startsWith('room_')) {
              roomIdToUse = parseInt(values.target_id.replace('room_', ''), 10);
            }
            
            await ChatService.sendMessage(roomIdToUse, {
              content,
              file_id: msgToForward?.file_id,
              reply_to_id: null
            });
            message.success('Сообщение переслано');
            setForwardModalOpen(false);
            forwardForm.resetFields();
            setMsgToForward(null);
          } catch (e) {
            message.error("Ошибка при пересылке");
          }
        }}>
          <Form.Item name="target_id" label="Кому переслать" rules={[{ required: true, message: 'Выберите чат или сотрудника' }]}>
            <Select
              showSearch
              placeholder="Выберите чат или сотрудника"
              options={[
                {
                  label: 'Чаты',
                  options: rooms.map(r => ({ value: `room_${r.id}`, label: r.name }))
                },
                {
                  label: 'Сотрудники',
                  options: users.filter(u => u.id !== user?.id).map(u => ({ value: `user_${u.id}`, label: u.full_name || u.email }))
                }
              ]}
              filterOption={(input, option) => {
                if (option?.options) return false;
                return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
    </div>
  );
}
